/**
 * 5 Question Screen renderer (artifact_name: `five_question_screen`,
 * tool `tool_five_question_internal_screen`). Renders FiveQuestionScreenOutput:
 * five scored dimensions, a signal summary, and the next step. Model = the
 * parsed JSON (lossless).
 */

import React from 'react';
import { CardShell } from './shared/components';
import {
  ActionsBlock,
  Divided,
  KV,
  Pill,
  Prose,
  Section,
  SourcesBlock,
  Table,
  actionsHtml,
  humanizeEnum,
  isRecord,
  makeNarrativeEditor,
  proseHtml,
  sectionHtml,
  sourcesHtml,
  str,
} from './shared/atlas';
import { htmlDoc } from './shared/html';
import type { ArtifactRenderer } from './types';

export type FiveScreenModel = Record<string, unknown>;

const arr = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter(isRecord) : [];

// -- parse / serialize -------------------------------------------------------

export function parseFiveScreen(json: Record<string, unknown>): FiveScreenModel | null {
  // Discriminated from 5 Whys (`whys`) by `dimensions` + `overall_signal`.
  if (!Array.isArray(json.dimensions)) return null;
  if (!('overall_signal' in json) && !isRecord(json.signal_summary)) return null;
  if (arr(json.dimensions).length === 0) return null;
  return json;
}

const fiveScreenToJson = (m: FiveScreenModel): Record<string, unknown> => m;

// -- card --------------------------------------------------------------------

const DimensionRow: React.FC<{ d: Record<string, unknown> }> = ({ d }) => (
  <div className="py-3">
    <div className="mb-1 flex items-center gap-2">
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
        {String(d.number ?? '')}
      </span>
      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {humanizeEnum(str(d.name))}
      </span>
      <Pill value={str(d.assessment)} className="ml-auto" />
    </div>
    {str(d.question) && (
      <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">{str(d.question)}</p>
    )}
    <Prose>{str(d.evidence)}</Prose>
    {str(d.if_weak_what_would_change) && (
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium">If weak: </span>
        {str(d.if_weak_what_would_change)}
      </p>
    )}
    {str(d.source) && <p className="mt-1 text-[11px] text-gray-400">{str(d.source)}</p>}
  </div>
);

const FiveScreenCard: React.FC<{ model: FiveScreenModel }> = ({ model }) => {
  const dims = arr(model.dimensions);
  const summary = isRecord(model.signal_summary) ? model.signal_summary : {};
  const summaryRows = arr(summary.dimensions);

  return (
    <CardShell testId="five-screen-artifact-card">
      <Section title="Screen" trailing={<Pill value={str(model.overall_signal)} />}>
        <Divided>
          {str(model.scope_name) && <KV label="Scope">{str(model.scope_name)}</KV>}
          {str(model.version) && <KV label="Version">{str(model.version)}</KV>}
        </Divided>
      </Section>

      <Section title="Five dimensions">
        <Divided>
          {dims.map((d, i) => (
            <DimensionRow key={i} d={d} />
          ))}
        </Divided>
      </Section>

      {(summaryRows.length > 0 || str(summary.weakest_dimension)) && (
        <Section
          title="Signal summary"
          trailing={
            str(summary.weakest_dimension) ? (
              <span className="text-xs text-gray-400">
                Weakest: {humanizeEnum(str(summary.weakest_dimension))}
              </span>
            ) : undefined
          }
        >
          {summaryRows.length > 0 && (
            <Table
              head={['#', 'Dimension', 'Assessment', 'Based on']}
              rows={summaryRows.map((r) => [
                String(r.number ?? ''),
                humanizeEnum(str(r.dimension)),
                <Pill value={str(r.assessment)} />,
                <span className="text-xs text-gray-500 dark:text-gray-400">{str(r.based_on)}</span>,
              ])}
            />
          )}
        </Section>
      )}

      {str(model.what_to_do_next) && (
        <Section title="What to do next">
          <Prose>{str(model.what_to_do_next)}</Prose>
        </Section>
      )}

      <SourcesBlock sources={model.sources} />
      <ActionsBlock actions={model.actions} />
    </CardShell>
  );
};

// -- editor ------------------------------------------------------------------

const FiveScreenEditor = makeNarrativeEditor<FiveScreenModel>(
  [
    {
      label: 'What to do next',
      rows: 3,
      get: (m) => str(m.what_to_do_next),
      set: (m, v) => (m.what_to_do_next = v),
    },
    {
      label: 'Actions',
      rows: 4,
      hint: 'one per line',
      get: (m) => (Array.isArray(m.actions) ? (m.actions as string[]).join('\n') : ''),
      set: (m, v) => (m.actions = v.split('\n').map((s) => s.trim()).filter(Boolean)),
    },
  ],
  fiveScreenToJson
);

// -- markdown / html ---------------------------------------------------------

export function fiveScreenToMarkdown(model: FiveScreenModel): string {
  const lines: string[] = [`# 5 Question Screen — ${humanizeEnum(str(model.overall_signal))}`];
  if (str(model.scope_name)) lines.push(`**Scope:** ${str(model.scope_name)} (v${str(model.version)})`);
  arr(model.dimensions).forEach((d) => {
    lines.push(`\n## ${d.number ?? ''}. ${humanizeEnum(str(d.name))} — ${humanizeEnum(str(d.assessment))}`);
    if (str(d.question)) lines.push(`_${str(d.question)}_`);
    if (str(d.evidence)) lines.push(str(d.evidence));
  });
  if (str(model.what_to_do_next)) lines.push(`\n## What to do next\n\n${str(model.what_to_do_next)}`);
  const actions = Array.isArray(model.actions) ? (model.actions as string[]) : [];
  if (actions.length) lines.push(`\n## Actions\n\n${actions.map((a) => `- ${a}`).join('\n')}`);
  return lines.join('\n');
}

export function fiveScreenToHtml(model: FiveScreenModel, title = '5 Question Screen'): string {
  const dimsHtml = arr(model.dimensions)
    .map((d) =>
      sectionHtml(
        `${d.number ?? ''}. ${humanizeEnum(str(d.name))} — ${humanizeEnum(str(d.assessment))}`,
        proseHtml(str(d.question)) + proseHtml(str(d.evidence))
      )
    )
    .join('');
  const body = [
    sectionHtml(
      '5 Question Screen',
      `<p><strong>${humanizeEnum(str(model.overall_signal))}</strong> — ${str(model.scope_name)} (v${str(model.version)})</p>`
    ),
    dimsHtml,
    str(model.what_to_do_next) ? sectionHtml('What to do next', proseHtml(str(model.what_to_do_next))) : '',
    sourcesHtml(model.sources),
    actionsHtml(model.actions),
  ].join('');
  return htmlDoc(title, body);
}

export const fiveQuestionScreenRenderer: ArtifactRenderer<FiveScreenModel> = {
  type: 'five_question_screen',
  matchNames: ['five_question_screen', 'five_question_internal_screen'],
  parse: parseFiveScreen,
  toJson: fiveScreenToJson,
  toMarkdown: fiveScreenToMarkdown,
  toHtml: fiveScreenToHtml,
  Card: FiveScreenCard,
  Editor: FiveScreenEditor,
};
